import LayerMixed from 'Simulations/LayerMixed';
import { getViewer } from 'store/viewerStore';
import { Controller } from './Controller';
import { DataState, NotificationType } from '@visualizer/types';
import usePersistentStore, { getGlobalState } from '@gui/store/persistent/persistentStore';
import { Panels } from '@gui/constants/Panels';
import useNotificationStore from '@gui/store/notification';

class MixerController extends Controller {
    private readonly mixerLayer: LayerMixed;

    constructor() {
        super();
        const mixerElements = usePersistentStore.getState().layers.layerStates.mixed?.mixerElements;
        this.mixerLayer = new LayerMixed(mixerElements);
    }

    protected updateStore(): void {
        const serializedMixer = this.mixerLayer.toJSON();
        usePersistentStore.getState().updateLayerState('mixed', { mixerElements: serializedMixer });
    }

    public getMixerLayer(): LayerMixed {
        return this.mixerLayer;
    }

    public run(): void {
        const isAndOr = getGlobalState().data.mixerAndLogic;
        // Refresh state (used for layers in the mixer to stay up to date with filtering in Data):
        this.mixerLayer.refreshInternalState();
        this.mixerLayer.getPipeline()?.clearCacheAll?.();
        this.mixerLayer.mixLayers(isAndOr);
        getViewer().refreshFrames();
    }
    public refreshInternalState() {
        this.mixerLayer.refreshInternalState();
    }
    public addLayer(layerId: string): void {
        const showNotification = useNotificationStore.getState().showNotification;
        const multimode = getGlobalState().data.multimode;
        const mixerElement = this.mixerLayer.addLayer(layerId);
        if (mixerElement) {
            this.updateStore();
            if (multimode) {
                getViewer().getInstanceRenderer()?.addLayerDraw(mixerElement);
            }
            showNotification('Correctly added the layer in the mixer!', NotificationType.Success);
        } else {
            showNotification('This layer is already in the mixer!', NotificationType.Error);
        }
    }

    public deleteLayer(layerId: string): void {
        const multimode = getGlobalState().data.multimode;

        this.mixerLayer.removeLayer(layerId);
        if (multimode) getViewer().getInstanceRenderer()?.removeLayerDraw(layerId);
        this.updateStore();

        if (multimode) getViewer().updateSensors();
    }

    public deleteAllLayers(): void {
        const multimode = getGlobalState().data.multimode;
        const layerIds = this.mixerLayer.getInternalMixerElements().map((element) => element.layerId);

        if (layerIds.length === 0) return;

        layerIds.forEach((layerId) => {
            this.mixerLayer.removeLayer(layerId);
            if (multimode) getViewer().getInstanceRenderer()?.removeLayerDraw(layerId);
        });
        this.updateStore();

        if (multimode) getViewer().updateSensors();
    }

    public changeWeight(layerId: string, weight: number): void {
        this.mixerLayer.changeWeight(layerId, weight);
        this.updateStore();
    }

    public toggleLock(layerId: string) {
        this.mixerLayer.toggleLock(layerId);
        this.updateStore();
    }

    public toggleVisible(layerId: string) {
        this.mixerLayer.toggleVisible(layerId);
        this.updateStore();
    }
    public toggleTransparency(layerId: string) {
        this.mixerLayer.toggleTransparency(layerId);
        const element = this.mixerLayer.getInternalMixerElements().find((el) => el.layerId === layerId);
        if (element?.frameDrawOptions?.transparent) {
            this.mixerLayer.setLayerTransparencyAmount(layerId, 0.5);
        }
        this.updateStore();
        if (usePersistentStore.getState().global.data.multimode) {
            getViewer().getInstanceRenderer()?.updateFrames();
        }
    }

    public setTransparencyAmount(amount: number, onlyTransparent = false) {
        this.mixerLayer.setTransparencyAmount(amount, { onlyTransparent });
        this.updateStore();
        getViewer().getInstanceRenderer()?.updateFrames();
    }

    public resetWeight() {
        this.mixerLayer.resetWeights();
        this.updateStore();
    }

    public toggleInvert(layerId: string) {
        this.mixerLayer.toggleInvert(layerId);
        this.updateStore();
    }

    public isZeroOneNormalization(layerId: string) {
        this.mixerLayer.toggleZeroOneNormalization(layerId);
        this.updateStore();
    }

    public toggleAndORLogic(next: 'and' | 'or') {
        const setDataPanel = (updates: Partial<DataState>) => usePersistentStore.getState().setPanel(updates, Panels.DATA);
        setDataPanel({ mixerAndLogic: next === 'and' });
        this.run();
    }

    public toggleHigherLogic(enabled: boolean) {
        const setDataPanel = (updates: Partial<DataState>) => usePersistentStore.getState().setPanel(updates, Panels.DATA);
        setDataPanel({ mixerHigherLogic: enabled });
        getViewer().refreshFrames();
    }

    public toggleMultiMode(multimode: boolean) {
        if (multimode) {
            // Refresh state also in multi:
            this.mixerLayer.refreshInternalState();
            getViewer().getInstanceRenderer()?.enableMultimode(this.mixerLayer.getInternalMixerElements());
        } else getViewer().getInstanceRenderer()?.disableMultimode();
    }

    public destroy() {
        this.mixerLayer.destroy();
    }
}

export default MixerController;
